import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useParams, useSearchParams, Link } from 'react-router-dom';
import { CheckCircle, Clock, MapPin, Calendar, User } from 'lucide-react';
import {
  bookingPublicApi,
  BookingType,
  BookingSlot,
  BookResponse,
} from '../../services/api';
import { brandingVars, brandingLogo, BookingBranding } from './bookingBranding';
import './Booking.css';

// ─── Helpers ─────────────────────────────────────────────────────────────────

/** Extract YYYY-MM-DD from a start_local ISO string (already in resource tz). */
const localDateKey = (startLocal: string): string => startLocal.split('T')[0];

/** Format time portion of start_local as "h:mm AM/PM". */
const formatLocalTime = (startLocal: string): string => {
  const timePart = (startLocal.split('T')[1] ?? '').split(/[+\-Z]/)[0];
  const [hourStr, minuteStr] = timePart.split(':');
  const hour = parseInt(hourStr ?? '0', 10);
  const minute = minuteStr ?? '00';
  const ampm = hour >= 12 ? 'PM' : 'AM';
  const displayHour = hour % 12 || 12;
  return `${displayHour}:${minute} ${ampm}`;
};

/** Format a YYYY-MM-DD string as "Tuesday, July 1". */
const formatDateLabel = (dateStr: string): string => {
  const [year, month, day] = dateStr.split('-').map(Number);
  const d = new Date(year, month - 1, day);
  return d.toLocaleDateString('en-US', {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
  });
};

/** Format a slot's start_local as a friendly full label. */
const formatSlotLabel = (startLocal: string): string => {
  const dateStr = localDateKey(startLocal);
  return `${formatDateLabel(dateStr)} at ${formatLocalTime(startLocal)}`;
};

/** Group slots by local calendar date key. */
const groupSlotsByDate = (slots: BookingSlot[]): [string, BookingSlot[]][] => {
  const map: Record<string, BookingSlot[]> = {};
  const order: string[] = [];
  for (const slot of slots) {
    const key = localDateKey(slot.start_local);
    if (!map[key]) {
      map[key] = [];
      order.push(key);
    }
    map[key].push(slot);
  }
  return order.map((key) => [key, map[key]]);
};

// ─── State machine ────────────────────────────────────────────────────────────

type PageState = 'loading' | 'error' | 'picking' | 'confirming' | 'confirmed';

// ─── PublicBooking ────────────────────────────────────────────────────────────

const PublicBooking: React.FC = () => {
  const { typeId } = useParams<{ typeId: string }>();
  const [searchParams] = useSearchParams();

  // Query-param prefills
  const prefillName = searchParams.get('name') ?? '';
  const prefillEmail = searchParams.get('email') ?? '';
  const formSubmissionId = searchParams.get('s') ?? undefined;
  const formId = searchParams.get('f') ?? undefined;

  const [pageState, setPageState] = useState<PageState>('loading');
  const [errorMsg, setErrorMsg] = useState('');

  const [bookingType, setBookingType] = useState<BookingType | null>(null);
  const [slots, setSlots] = useState<BookingSlot[]>([]);
  const [timezone, setTimezone] = useState('');
  const [branding, setBranding] = useState<BookingBranding | null>(null);
  const bkStyle = useMemo(() => brandingVars(branding), [branding]);
  const logoUrl = brandingLogo(branding);

  const [selectedSlot, setSelectedSlot] = useState<BookingSlot | null>(null);

  const [name, setName] = useState(prefillName);
  const [email, setEmail] = useState(prefillEmail);
  const [phone, setPhone] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');

  const [confirmed, setConfirmed] = useState<BookResponse | null>(null);

  const loadSlots = useCallback(async (tId: string) => {
    const slotsRes = await bookingPublicApi.getSlots(tId);
    setSlots(slotsRes.slots);
    setTimezone(slotsRes.timezone);
  }, []);

  const load = useCallback(async () => {
    if (!typeId) {
      setErrorMsg('Invalid booking link.');
      setPageState('error');
      return;
    }
    try {
      const [typeRes, slotsRes] = await Promise.all([
        bookingPublicApi.getType(typeId),
        bookingPublicApi.getSlots(typeId),
      ]);
      setBookingType(typeRes);
      setBranding(typeRes.branding ?? null);
      setSlots(slotsRes.slots);
      setTimezone(slotsRes.timezone);
      setPageState('picking');
    } catch (err: any) {
      const status = err?.response?.status;
      if (status === 404) {
        setErrorMsg('This booking type was not found or is no longer available.');
      } else {
        setErrorMsg('Failed to load booking page. Please try again.');
      }
      setPageState('error');
    }
  }, [typeId]);

  useEffect(() => {
    load();
  }, [load]);

  const handleSelectSlot = (slot: BookingSlot) => {
    setSelectedSlot(slot);
    setSubmitError('');
    setPageState('confirming');
  };

  const handleBack = () => {
    setSelectedSlot(null);
    setSubmitError('');
    setPageState('picking');
  };

  const handleBook = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!typeId || !selectedSlot) return;
    if (bookingType?.require_phone && !phone.trim()) {
      setSubmitError('Phone number is required for this booking type');
      return;
    }
    setSubmitting(true);
    setSubmitError('');
    try {
      const res = await bookingPublicApi.book(typeId, {
        start_utc: selectedSlot.start_utc,
        attendee_name: name.trim() || undefined,
        attendee_email: email.trim() || undefined,
        attendee_phone: phone.trim() || undefined,
        form_submission_id: formSubmissionId,
        form_id: formId,
      });
      setConfirmed(res);
      setPageState('confirmed');
    } catch (err: any) {
      const status = err?.response?.status;
      if (status === 409) {
        setSubmitError(
          'That time slot was just taken by someone else. Please go back and pick another time.'
        );
        // Refresh slots so taken slot disappears
        if (typeId) {
          loadSlots(typeId).catch(() => {});
        }
      } else if (status === 429) {
        setSubmitError('Too many booking attempts. Please wait a moment and try again.');
      } else {
        setSubmitError(
          err?.response?.data?.detail ?? 'Booking failed. Please try again.'
        );
      }
    } finally {
      setSubmitting(false);
    }
  };

  // ─ Loading ─
  // (branding not yet known — correctly renders unbranded)
  if (pageState === 'loading') {
    return (
      <div className="bk-page bk-scope">
        <div className="bk-card">
          <p className="bk-desc" style={{ textAlign: 'center' }}>Loading...</p>
        </div>
      </div>
    );
  }

  // ─ Error ─
  // (branding not yet known — correctly renders unbranded)
  if (pageState === 'error') {
    return (
      <div className="bk-page bk-scope">
        <div className="bk-card">
          <h1 className="bk-title">Unavailable</h1>
          <p className="bk-desc">{errorMsg}</p>
        </div>
      </div>
    );
  }

  // ─ Confirmed ─
  if (pageState === 'confirmed' && confirmed) {
    return (
      <div className="bk-page bk-scope" style={bkStyle}>
        <div className="bk-card">
          {logoUrl && (
            <img
              className="bk-logo"
              src={logoUrl}
              alt=""
              onError={(e) => { e.currentTarget.style.display = 'none'; }}
            />
          )}
          <div className="bk-success-icon">
            <CheckCircle size={52} />
          </div>
          <h2 className="bk-success-title">Booking Confirmed!</h2>
          <p className="bk-success-msg">
            {confirmed.message ?? "You're all set. A confirmation has been recorded."}
          </p>

          <div className="bk-confirm-detail">
            {bookingType && (
              <div className="bk-confirm-detail-row">
                <span className="bk-confirm-detail-label">Meeting</span>
                <span>{bookingType.name}</span>
              </div>
            )}
            <div className="bk-confirm-detail-row">
              <span className="bk-confirm-detail-label">When</span>
              <span>{formatSlotLabel(confirmed.start_local)}</span>
            </div>
            {confirmed.timezone && (
              <div className="bk-confirm-detail-row">
                <span className="bk-confirm-detail-label">Timezone</span>
                <span>{confirmed.timezone}</span>
              </div>
            )}
            {confirmed.location && (
              <div className="bk-confirm-detail-row">
                <span className="bk-confirm-detail-label">Location</span>
                <span>{confirmed.location}</span>
              </div>
            )}
          </div>

          <Link
            className="bk-manage-link"
            to={`/booking/manage/${confirmed.manage_token}`}
          >
            Manage or cancel your booking
          </Link>
        </div>
      </div>
    );
  }

  // ─ Confirming (form) ─
  if (pageState === 'confirming' && selectedSlot) {
    return (
      <div className="bk-page bk-scope" style={bkStyle}>
        <div className="bk-card">
          {logoUrl && (
            <img
              className="bk-logo"
              src={logoUrl}
              alt=""
              onError={(e) => { e.currentTarget.style.display = 'none'; }}
            />
          )}
          {bookingType && (
            <>
              <h1 className="bk-title">{bookingType.name}</h1>
              {bookingType.resource_name && (
                <p className="bk-subtitle">with {bookingType.resource_name}</p>
              )}
            </>
          )}

          <div className="bk-confirm-section">
            <p className="bk-section-label">Your selected time</p>
            <div className="bk-selected-slot">
              {formatSlotLabel(selectedSlot.start_local)}
              {timezone && ` (${timezone})`}
            </div>

            <form onSubmit={handleBook} noValidate>
              <div className="bk-field">
                <label className="bk-label" htmlFor="bk-name">Name</label>
                <input
                  id="bk-name"
                  className="bk-input"
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Your name"
                />
              </div>
              <div className="bk-field">
                <label className="bk-label" htmlFor="bk-email">Email</label>
                <input
                  id="bk-email"
                  className="bk-input"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="your@email.com"
                />
              </div>
              <div className="bk-field">
                <label className="bk-label" htmlFor="bk-phone">
                  Phone {!bookingType?.require_phone && (
                    <span style={{ fontWeight: 400, color: 'rgba(15, 23, 42, 0.62)' }}>(optional)</span>
                  )}
                </label>
                <input
                  id="bk-phone"
                  className="bk-input"
                  type="tel"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  placeholder="+1 555 000 0000"
                  required={bookingType?.require_phone}
                />
              </div>

              {submitError && <p className="bk-error">{submitError}</p>}

              <div className="bk-actions">
                <button
                  type="button"
                  className="bk-btn-secondary"
                  onClick={handleBack}
                  disabled={submitting}
                >
                  Back
                </button>
                <button
                  type="submit"
                  className="bk-btn-primary"
                  disabled={submitting}
                >
                  {submitting ? 'Booking...' : 'Book'}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    );
  }

  // ─ Slot picker ─
  const dateGroups = groupSlotsByDate(slots);

  return (
    <div className="bk-page bk-scope" style={bkStyle}>
      <div className="bk-card">
        {logoUrl && (
            <img
              className="bk-logo"
              src={logoUrl}
              alt=""
              onError={(e) => { e.currentTarget.style.display = 'none'; }}
            />
          )}
        {bookingType && (
          <>
            <h1 className="bk-title">{bookingType.name}</h1>
            {bookingType.resource_name && (
              <p className="bk-subtitle">with {bookingType.resource_name}</p>
            )}
            {bookingType.description && (
              <p className="bk-desc">{bookingType.description}</p>
            )}

            <div className="bk-meta">
              <span className="bk-meta-item">
                <Clock size={14} />
                {bookingType.duration_minutes} min
              </span>
              {bookingType.location && (
                <span className="bk-meta-item">
                  <MapPin size={14} />
                  {bookingType.location}
                </span>
              )}
              {timezone && (
                <span className="bk-meta-item">
                  <Calendar size={14} />
                  {timezone}
                </span>
              )}
            </div>
          </>
        )}

        <p className="bk-section-label">Select a time</p>

        {dateGroups.length === 0 ? (
          <div className="bk-empty">
            <User size={32} style={{ marginBottom: 8, opacity: 0.4 }} />
            <p>No availability at the moment. Please check back later.</p>
          </div>
        ) : (
          <div className="bk-slots-scroll">
            <div className="bk-date-list">
              {dateGroups.map(([dateKey, dateSlots]) => (
                <div key={dateKey} className="bk-date-group">
                  <div className="bk-date-header">{formatDateLabel(dateKey)}</div>
                  <div className="bk-slot-grid">
                    {dateSlots.map((slot) => (
                      <button
                        key={slot.start_utc}
                        type="button"
                        className="bk-slot-btn"
                        onClick={() => handleSelectSlot(slot)}
                      >
                        {formatLocalTime(slot.start_local)}
                      </button>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PublicBooking;
